import * as React from 'react';
import { View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { useSharedValue } from 'react-native-reanimated';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { triggerChartSelectionFeedback } from 'utils/hapticFeedback';
import StockCandleBar from './StockCandleBar';
import { ACTUAL_CANDLE_CHART_HEIGHT } from '../constants';
import { isWeb } from '../../../constants';
import SelectedCandleInfoView from './SelectedCandleInfoView';
import CandleMaxPriceIndicator from './CandleMaxPriceIndicator';
import CandleMinPriceIndicator from './CandleMinPriceIndicator';
import CandleSize from './CandleSize';
const totalPaddingHorizontal = rem(32);
const StockCandleChart = ({ dataObj, selectedInfoObj }) => {
    const styles = useStyles(styleSet);
    const isGestureActive = useSharedValue(false);
    const { width } = useAppFrameDimensions(isWeb);
    const chartWidth = width - totalPaddingHorizontal;
    const eachCandleWidth = chartWidth / (dataObj.candleData.length + 1);
    const totalWidth = chartWidth - eachCandleWidth;
    const onEnd = React.useCallback(() => {
        'worklet';
        isGestureActive.value = false;
        // eslint-disable-next-line no-param-reassign
        selectedInfoObj.value = undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInfoObj]);
    const onChange = React.useCallback((event) => {
        'worklet';
        if (!isGestureActive.value) {
            triggerChartSelectionFeedback();
        }
        isGestureActive.value = true;
        const computedX = (event.x - eachCandleWidth) / totalWidth;
        const index = dataObj.candleData.findIndex((point) => point && point.x >= computedX);
        if (index >= 0 && dataObj.candleData[index]) {
            // eslint-disable-next-line no-param-reassign
            selectedInfoObj.value = dataObj.candleData[index];
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataObj.candleData, eachCandleWidth, totalWidth]);
    const onDrag = React.useMemo(() => Gesture.Simultaneous(Gesture.Pan().onChange(onChange).onEnd(onEnd), Gesture.LongPress()
        .onBegin(onChange)
        .onStart(onChange)
        .onTouchesCancelled(onEnd)
        .onEnd(onEnd)
        .onTouchesCancelled(onEnd)
        .minDuration(0)
        .maxDistance(100000)
        .shouldCancelWhenOutside(false)), [onChange, onEnd]);
    return (<View style={styles.flex}>
      <View style={styles.timeFrameContainer}>
        <CandleSize selectedInfoObj={selectedInfoObj}/>
      </View>
      <View style={styles.container}>
        <GestureDetector gesture={onDrag}>
          <Animated.View style={styles.candleContainer}>
            <View style={styles.candlePaddingHorizontal}/>
            {dataObj.candleData.map((eachData, index) => (<StockCandleBar key={index} timeStamp={eachData?.timeStamp} selectedInfoObj={selectedInfoObj} priceTrend={eachData?.priceTrend} priceHighLowTop={eachData?.priceHighLowTop} priceOpenCloseTop={eachData?.priceOpenCloseTop} priceHighLowHeight={eachData?.priceHighLowHeight} priceOpenCloseHeight={eachData?.priceOpenCloseHeight}/>))}
            <View style={styles.candlePaddingHorizontal}/>
          </Animated.View>
        </GestureDetector>
        {dataObj.maxCandlePoint ? (<CandleMaxPriceIndicator eachCandleWidth={eachCandleWidth} selectedInfoObj={selectedInfoObj} maxPoint={dataObj.maxCandlePoint}/>) : null}
        {dataObj.minCandlePoint ? (<CandleMinPriceIndicator eachCandleWidth={eachCandleWidth} selectedInfoObj={selectedInfoObj} minPoint={dataObj.minCandlePoint}/>) : null}
        <SelectedCandleInfoView eachCandleWidth={eachCandleWidth} selectedInfoObj={selectedInfoObj} totalCandles={dataObj.candleData?.length}/>
      </View>
    </View>);
};
export default React.memo(StockCandleChart);
const styleSet = createStyleSheets(() => ({
    flex: {
        flex: 1,
    },
    container: {
        flex: 1,
        justifyContent: 'center',
    },
    candlePaddingHorizontal: {
        flex: 0.5,
    },
    candleContainer: {
        flexDirection: 'row',
        height: ACTUAL_CANDLE_CHART_HEIGHT,
    },
    timeFrameContainer: {
        height: rem(25),
    },
}));
